import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { SkipLink } from '../components/a11y';
import { LanguagePicker } from '../components/LanguagePicker';
import { PasswordField, passwordMeetsPolicy } from '../components/PasswordField';

type Mode = 'signin' | 'signup' | 'confirm' | 'forgot' | 'forgot-confirm';
type FieldName = 'email' | 'code' | 'password' | 'newPassword';
type FieldErrors = Partial<Record<FieldName, string>>;

const COPY: Record<Mode, { eyebrowKey: string; titleKey: string }> = {
  signin: { eyebrowKey: 'login.welcomeBackEyebrow', titleKey: 'login.welcomeBackTitle' },
  signup: { eyebrowKey: 'login.beginEyebrow', titleKey: 'login.beginTitle' },
  confirm: { eyebrowKey: 'login.almostThereEyebrow', titleKey: 'login.almostThereTitle' },
  forgot: { eyebrowKey: 'login.resetEyebrow', titleKey: 'login.resetTitle' },
  'forgot-confirm': { eyebrowKey: 'login.resetEyebrow', titleKey: 'login.newPasswordTitle' },
};

const FIELD_ORDER: FieldName[] = ['email', 'code', 'password', 'newPassword'];
const FIELD_IDS: Record<FieldName, string> = {
  email: 'login-email',
  code: 'login-code',
  password: 'login-password',
  newPassword: 'login-new-password',
};
// Deliberately loose -- Cognito is the authority; this only catches a missing "@" or domain.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Sign in / sign up / confirm / reset. Review fix F11: field-level validation (the form keeps
 * `noValidate` so browser bubbles don't compete, but every field is now checked here, with
 * aria-invalid + aria-describedby and focus moved to the first problem), the Cognito password
 * requirements shown while typing, a show-password toggle, announced status/errors, a language
 * picker before sign-in, Google on both sign-in and sign-up, and links to Privacy/Terms.
 */
export function Login({ initialMode = 'signin' }: { initialMode?: 'signin' | 'signup' }) {
  const { signIn, signUp, confirmSignUp, resendCode, forgotPassword, confirmForgotPassword, googleSignIn } = useAuth();
  const { t } = useTranslation();

  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    document.title = `${t(COPY[mode].titleKey)} · Omnist`;
  }, [mode, t]);

  const checkNewPassword = (value: string): string | undefined => {
    if (!value) return t('auth.errors.passwordRequired');
    if (value !== value.trim()) return t('auth.errors.passwordSpaces');
    if (!passwordMeetsPolicy(value)) return t('auth.errors.passwordRules');
    return undefined;
  };

  const validate = (): FieldErrors => {
    const errs: FieldErrors = {};
    if (mode === 'signin' || mode === 'signup' || mode === 'forgot') {
      const value = email.trim();
      if (!value) errs.email = t('auth.errors.emailRequired');
      else if (!EMAIL_PATTERN.test(value)) errs.email = t('auth.errors.emailInvalid');
    }
    if ((mode === 'confirm' || mode === 'forgot-confirm') && !/^\d{6}$/.test(code.trim())) {
      errs.code = t('auth.errors.codeInvalid');
    }
    if (mode === 'signin' && !password) errs.password = t('auth.errors.passwordRequired');
    if (mode === 'signup') errs.password = checkNewPassword(password);
    if (mode === 'forgot-confirm') errs.newPassword = checkNewPassword(newPassword);
    return Object.fromEntries(Object.entries(errs).filter(([, v]) => v)) as FieldErrors;
  };

  // After a failed submit, re-check as the person types so each error clears as soon as it's fixed.
  useEffect(() => {
    if (!attempted) return;
    const errs = validate();
    setFieldErrors(errs);
    if (!FIELD_ORDER.some((f) => errs[f])) {
      const summary = t('auth.errors.summary');
      setError((prev) => (prev === summary ? null : prev));
    }
  }, [attempted, mode, email, password, code, newPassword]); // eslint-disable-line

  const goTo = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
    setFieldErrors({});
    setAttempted(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);

    const errs = validate();
    setFieldErrors(errs);
    const firstInvalid = FIELD_ORDER.find((f) => errs[f]);
    if (firstInvalid) {
      setAttempted(true);
      setError(t('auth.errors.summary'));
      document.getElementById(FIELD_IDS[firstInvalid])?.focus();
      return;
    }

    setAttempted(false);
    setIsSubmitting(true);
    const trimmedEmail = email.trim();
    try {
      if (mode === 'signin') {
        await signIn(trimmedEmail, password);
      } else if (mode === 'signup') {
        await signUp(trimmedEmail, password);
        setMode('confirm');
        setNotice(t('login.codeSent'));
      } else if (mode === 'confirm') {
        await confirmSignUp(trimmedEmail, code.trim());
        setNotice(t('login.emailConfirmed'));
        setMode('signin');
      } else if (mode === 'forgot') {
        await forgotPassword(trimmedEmail);
        setMode('forgot-confirm');
        setNotice(t('login.codeSent'));
      } else if (mode === 'forgot-confirm') {
        await confirmForgotPassword(trimmedEmail, code.trim(), newPassword);
        setNotice(t('login.passwordReset'));
        setMode('signin');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.somethingWentWrong'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setNotice(null);
    try {
      await resendCode(email.trim());
      setNotice(t('login.codeResent'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.somethingWentWrong'));
    }
  };

  const { eyebrowKey, titleKey } = COPY[mode];
  const errorId = (field: FieldName) => `${FIELD_IDS[field]}-error`;
  const newTab = <span className="visually-hidden"> {t('a11y.opensInNewTab')}</span>;

  return (
    <div className="login-layout">
      <SkipLink />
      <main id="main-content" className="login-panel" tabIndex={-1}>
        <div className="login-form-wrap">
          <div className="login-topline">
            <Link to="/" className="login-brand">
              <span className="logo-badge">
                <img src="/logo-face.png" alt="" className="logo-face" />
              </span>
              <span className="wordmark">Omnist</span>
            </Link>
            <LanguagePicker id="login-language" />
          </div>

          {(mode === 'signin' || mode === 'signup') && (
            <p className="login-intro">
              {t('auth.intro')} <Link to="/how-it-works">{t('auth.learnMore')}</Link>
            </p>
          )}

          <p className="login-eyebrow">{t(eyebrowKey)}</p>
          <h1 className="login-title wordmark">{t(titleKey)}</h1>
          {mode === 'signup' && (
            <p className="login-subnote">
              {t('auth.signupPitch')}{' '}
              <a href="/pricing" target="_blank" rel="noopener">{t('auth.links.pricing')}{newTab}</a>
            </p>
          )}

          {(mode === 'signin' || mode === 'signup') && (
            <>
              <button type="button" className="google-btn" onClick={googleSignIn}>
                <GoogleIcon /> {mode === 'signup' ? t('auth.signUpWithGoogle') : t('login.continueWithGoogle')}
              </button>
              <div className="login-divider"><span>{t('auth.orEmail')}</span></div>
            </>
          )}

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            {(mode === 'signin' || mode === 'signup' || mode === 'forgot') && (
              <div className="login-field">
                <label className="login-label" htmlFor={FIELD_IDS.email}>{t('login.email')}</label>
                <input
                  id={FIELD_IDS.email}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-input"
                  aria-invalid={fieldErrors.email ? true : undefined}
                  aria-describedby={fieldErrors.email ? errorId('email') : undefined}
                />
                {fieldErrors.email && <p id={errorId('email')} className="field-error">{fieldErrors.email}</p>}
              </div>
            )}

            {(mode === 'confirm' || mode === 'forgot-confirm') && (
              <div className="login-field">
                <label className="login-label" htmlFor={FIELD_IDS.code}>{t('login.code')}</label>
                <input
                  id={FIELD_IDS.code}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="login-input"
                  aria-invalid={fieldErrors.code ? true : undefined}
                  aria-describedby={fieldErrors.code ? errorId('code') : undefined}
                />
                {fieldErrors.code && <p id={errorId('code')} className="field-error">{fieldErrors.code}</p>}
              </div>
            )}

            {(mode === 'signin' || mode === 'signup') && (
              <PasswordField
                id={FIELD_IDS.password}
                label={t('login.password')}
                value={password}
                onChange={setPassword}
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                showRules={mode === 'signup'}
                error={fieldErrors.password}
              />
            )}

            {mode === 'forgot-confirm' && (
              <PasswordField
                id={FIELD_IDS.newPassword}
                label={t('login.newPassword')}
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
                showRules
                error={fieldErrors.newPassword}
              />
            )}

            {mode === 'signin' && (
              <button type="button" className="login-link" onClick={() => goTo('forgot')}>
                {t('login.forgotPassword')}
              </button>
            )}
            {mode === 'confirm' && (
              <button type="button" className="login-link" onClick={handleResend}>
                {t('login.resendCode')}
              </button>
            )}

            {/* Always-mounted live regions, so a new message is announced when it appears. */}
            <div role="alert" className="login-messages">
              {error && <p className="login-error">{error}</p>}
            </div>
            <div role="status" className="login-messages">
              {notice && !error && <p className="login-notice">{notice}</p>}
            </div>

            <button type="submit" className="btn btn-primary login-submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.pleaseWait') : t(SUBMIT_LABEL_KEY[mode])}
            </button>
          </form>

          {mode === 'signup' && (
            <p className="login-legal">
              {t('auth.legalNotice')}{' '}
              <a href="/privacy" target="_blank" rel="noopener">{t('auth.links.privacy')}{newTab}</a>
              {' · '}
              <a href="/terms" target="_blank" rel="noopener">{t('auth.links.terms')}{newTab}</a>
            </p>
          )}

          <p className="login-switch">
            {mode === 'signin' && (
              <>{t('login.needAccount')} <button type="button" className="login-link" onClick={() => goTo('signup')}>{t('login.signUp')}</button></>
            )}
            {(mode === 'signup' || mode === 'confirm' || mode === 'forgot' || mode === 'forgot-confirm') && (
              <>{t('login.alreadyHaveAccount')} <button type="button" className="login-link" onClick={() => goTo('signin')}>{t('login.signIn')}</button></>
            )}
          </p>

          <nav aria-label={t('auth.footerLabel')}>
            <ul className="login-footer-links">
              <li><Link to="/how-it-works">{t('auth.links.howItWorks')}</Link></li>
              <li><Link to="/pricing">{t('auth.links.pricing')}</Link></li>
              <li><Link to="/privacy">{t('auth.links.privacy')}</Link></li>
              <li><Link to="/terms">{t('auth.links.terms')}</Link></li>
              <li><Link to="/support">{t('auth.links.support')}</Link></li>
            </ul>
          </nav>
        </div>
      </main>

      <div className="login-hero-panel" aria-hidden="true">
        <img src="/login-hero.jpg" alt="" className="login-hero-image" />
        <p className="login-tagline wordmark">{t('login.tagline')}</p>
      </div>
    </div>
  );
}

const SUBMIT_LABEL_KEY: Record<Mode, string> = {
  signin: 'login.signIn',
  signup: 'login.createAccount',
  confirm: 'login.confirmEmail',
  forgot: 'login.sendCode',
  'forgot-confirm': 'login.resetPassword',
};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}
